import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react'

import { makeServerProps } from '@/server/query'

import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import makePage from '@/client/services/pages/makePage'
import {
  hasSummerCampEnded,
  SUMMER_CAMP_HERO_LOGO_SRC,
  SUMMER_CAMP_YEAR,
} from '@/client/services/summerCamp'
import { getSummerCampUrl } from '@/client/services/url'

import Layout from '@/components/Layout/Layout'
import PlatformHeader from '@/components/Layout/PlatformHeader'
import PublicImage from '@/components/PublicImage/PublicImage'
import SeriesBoxFromQuery from '@/components/Series/SeriesBox/SeriesBoxFromQuery'
import { useInfiniteScroll } from '@/components/Session/helpers'
import SessionBoxWithOutline from '@/components/Session/SessionBox/SessionBoxWithOutline'
import ShwLink from '@/components/ShwLink'
import Tabs from '@/components/Tabs/Tabs'
import useUrlTabController from '@/components/Tabs/useUrlTabController'
import useWorldFinder from '@/components/Worlds/hooks/world/useWorldFinder'

const SummerCampExploreIndex: React.FC = () => {
  const query = useQuery()
  const currentUser = useUser()

  type SessionDateFilterTab = 'Past' | 'Upcoming'

  const { worldSlug, WorldFinder } = useWorldFinder({})

  const [limit, handleLoadMore] = useInfiniteScroll({
    queryKey: 'limit',
    defaultValue: 12,
  })

  const [currentSessionsTab, sessionsTabToUrl] = useUrlTabController<SessionDateFilterTab>({
    queryKey: 'filter',
    tabToQueryParam: {
      Upcoming: 'upcoming',
      Past: 'past',
    },
    defaultTab: 'Upcoming',
  })

  const dateQuery =
    currentSessionsTab === 'Upcoming'
      ? SQL`${SessionQ.start_date} > NOW()`
      : SQL`${SessionQ.start_date} <= NOW()`

  const sessionsWhere = SQL`FROM (SELECT
      DISTINCT ON (
          CASE WHEN ${SessionQ.series_id} IS NULL
            THEN CONCAT_WS('session', ${SessionQ.id})
            ELSE CONCAT_WS('series', ${SessionQ.series_id})
          END
      )
    ${SessionQ}.* ${SessionQ.where(
      {
        include: [
          {
            association: 'Series',
            required: false,
          },
          {
            association: 'World',
            required: true,
            where: worldSlug == null ? SQL`TRUE` : SQL`${WorldQ.slug} = ${worldSlug}`,
          },
        ],
      },
      SQL`
        ${SeriesQ.isSummerCampSeries()}
        AND (${dateQuery})
        AND ${SessionQ.hasRoomForLearners()}
        AND ${SessionQ.userCanViewSession(currentUser.id)}
        AND ${SeriesQ.userCanViewSeries(currentUser.id, { disableBucketCheck: currentUser.isLoggedOut })}
        AND ${SeriesQ.enrollment_closed_at} IS NULL`
    )}) as ${SessionQ}`

  const sessionsCount = query<number>`COUNT(*) ${sessionsWhere}` ?? 0

  const sessions = query.many`${sessionsWhere} ORDER BY ${SessionQ.start_date} ASC LIMIT ${limit}`

  return (
    <Layout
      title="Summer Camp Catalog"
      header={
        <PlatformHeader
          heading={{
            title: 'Explore Sessions',
            breadcrumbs: [
              {
                url: getSummerCampUrl(),
                label: 'Summer Camp',
              },
            ],
          }}
        />
      }
    >
      <Container>
        <Stack direction="column" alignItems="center">
          <Box mb="4px">
            <ShwLink href={getSummerCampUrl()}>
              <PublicImage src={SUMMER_CAMP_HERO_LOGO_SRC} width="250px" maxWidth="100%" />
            </ShwLink>
          </Box>
          <Heading as="h2" size="2xl">
            Explore Summer Camp Series
          </Heading>
        </Stack>
        <Stack
          my="48px"
          direction={['column', 'column', 'row']}
          justifyContent="space-between"
          spacing="16px"
        >
          <WorldFinder />
          <Spacer />
          <Box>
            <Tabs<SessionDateFilterTab>
              variant="rounded"
              currentTab={currentSessionsTab}
              tabToUrl={sessionsTabToUrl}
              tabs={['Upcoming', 'Past']}
            />
          </Box>
        </Stack>
        {hasSummerCampEnded() ? (
          <Text fontWeight="bold" color="fg.muted">
            Summer Camp {SUMMER_CAMP_YEAR} was a smashing success! Stay tuned for{' '}
            {SUMMER_CAMP_YEAR + 1}.
          </Text>
        ) : (
          <>
            <SimpleGrid spacing="16px" columns={[1, 1, 2, 3]}>
              {sessions.map(session => {
                const seriesId = session(SessionQ.series_id)
                const series = session.one(SessionQ.getSeries())
                return (
                  <Box key={session(SessionQ.id) ?? 0}>
                    <Box display={seriesId == null ? 'block' : 'none'}>
                      <SessionBoxWithOutline session={session} hideEntrypoint={false} />
                    </Box>
                    <Box display={seriesId == null ? 'none' : 'block'}>
                      <SeriesBoxFromQuery series={series} />
                    </Box>
                  </Box>
                )
              })}
            </SimpleGrid>
            {sessionsCount === 0 ? (
              <Text textAlign="center">
                There are no sessions matching your criteria at the moment.
              </Text>
            ) : null}
            {sessionsCount > 0 && limit != null ? (
              <Flex justifyContent="center" mt={6}>
                {sessionsCount > limit ? (
                  <Button minWidth="150px" onClick={handleLoadMore}>
                    See More
                  </Button>
                ) : null}
              </Flex>
            ) : null}
          </>
        )}
      </Container>
    </Layout>
  )
}

const SummerCampExploreIndexPage = makePage(SummerCampExploreIndex, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(SummerCampExploreIndexPage)
export default SummerCampExploreIndexPage
